/**
 * @typedef {keyof typeof fr} MessageKey
 * Le français est le catalogue de référence. Les tests imposent la parité exacte des clés.
 */
export const fr = Object.freeze({
  "meta.title": "Ça se prend ? — Calculateur de belote",
  "meta.description": "Estimez une prise à la belote classique, puis entraînez-vous avec le même moteur, sans compte.",
  "nav.home": "Accueil Ça se prend ?",
  "nav.practice": "JOUER UNE DONNE",
  "nav.observer": "OBSERVER & APPRENDRE",
  "nav.install": "INSTALLEZ L’APP",
  "nav.demo": "TESTEZ L’APP",
  "nav.rule": "Belote classique · sans annonces",
  "nav.language": "Langue",
  "demo.banner": "Démonstration — données fictives",
  "demo.detail": "Même moteur, mêmes règles et même build. Les données de cette session sont isolées sur cet appareil.",
  "demo.reset": "RÉINITIALISER LA DÉMO",
  "demo.exit": "QUITTER LA DÉMO",
  "hero.eyebrow": "Aide à la prise",
  "hero.title": "Le bon atout,<br><em>avant de dire oui.</em>",
  "hero.copy": "Renseignez les cinq cartes reçues, la retourne et votre place. Le simulateur joue des milliers de donnes possibles pour estimer vos chances de réussir le contrat.",
  "calculator.kicker": "Votre donne",
  "calculator.title": "Composez votre jeu",
  "calculator.reset": "Tout effacer",
  "calculator.hand": "Ma main",
  "calculator.center": "La retourne",
  "calculator.fillZone": "Zone à remplir",
  "calculator.cardPicker": "Sélection des cartes",
  "calculator.positionAria": "Position à la table",
  "calculator.placeKicker": "Votre place",
  "calculator.placeTitle": "Où êtes-vous assis ?",
  "calculator.dealer": "Donneur",
  "calculator.first": "Premier",
  "calculator.partner": "Part. donneur",
  "calculator.last": "Dernier",
  "calculator.precision": "Précision",
  "calculator.precision.fast": "Rapide · 2 000 donnes",
  "calculator.precision.balanced": "Équilibrée · 5 000 donnes",
  "calculator.precision.fine": "Fine · 10 000 donnes",
  "calculator.auto": "Calcul automatique",
  "calculator.auto.initial": "Sélectionnez vos cartes et votre place",
  "calculator.progress": "Simulation en cours…",
  "results.kicker": "Verdict",
  "results.title": "Voici ce que dit la donne",
  "method.summary": "Comment la probabilité est-elle calculée ?",
  "method.first": "Chaque simulation complète aléatoirement les quatre mains, puis joue les huit plis en respectant la fourniture, la coupe, la surcoupe, l’ordre des cartes et le dix de der. La position et les passes déjà entendues modifient les donnes considérées comme plausibles.",
  "method.second": "Le contrat est réussi lorsque l’équipe preneuse finit devant la défense. La belote-rebelote est comptée ; tierces, cinquantes et carrés ne le sont pas. Le résultat reste une estimation d’aide à la décision : le style réel des quatre joueurs peut la faire varier.",
  "footer.product": "Calculateur de prise · Belote à 4 joueurs",
  "footer.rules": "32 cartes · 2 équipes · 8 plis",
  "footer.version": "Version {version} · build {buildId}",
  "install.title": "Installer Ça se prend ?",
  "install.ready": "Ajoutez l’app à votre écran d’accueil pour l’utiliser en plein écran, même hors ligne.",
  "install.ios": "Dans Safari, touchez Partager puis « Sur l’écran d’accueil ».",
  "install.desktop": "Ouvrez le menu de votre navigateur puis choisissez « Installer l’application ».",
  "install.confirm": "INSTALLER MAINTENANT",
  "install.close": "Plus tard",
  "install.done": "L’application est déjà installée.",
  "common.close": "Fermer",
  "common.none": "—",
  "common.error": "Une erreur est survenue.",
  "common.points": "{value} points moyens",
  "common.tricks": "{value} / 8 plis moyens",
  "common.margin": "± {value} marge estimée",
  "common.percent": "{value} pour cent",
  "suit.clubs": "Trèfle",
  "suit.diamonds": "Carreau",
  "suit.hearts": "Cœur",
  "suit.spades": "Pique",
  "app.running": "Simulation en cours…",
  "app.missing.one": "Encore {count} carte",
  "app.missing.other": "Encore {count} cartes",
  "app.choosePlace": "Choisissez votre place",
  "app.ready": "Prêt à recalculer à chaque changement",
  "app.cardLabel": "{rank} de {suit}",
  "app.personalProfile": "Profil personnel {value} %",
  "app.result.returned": "Si je prends à la couleur",
  "app.result.alternative": "Meilleure autre couleur",
  "app.comparison": "Comparaison des quatre atouts",
  "app.returnedSuffix": " · retourne",
  "app.personalSuffix": "{value} % perso",
  "app.simulations": "{count} donnes par couleur",
  "app.verdict": "Votre prise en position {position}",
  "app.recommend.alternativeTitle": "{symbol} {suit} est la meilleure possibilité",
  "app.recommend.alternativeCopy": "{alternative} % contre {returned} % à la couleur retournée. Il faudrait passer le premier tour pour pouvoir la choisir.",
  "app.recommend.returnedTitle": "La couleur retournée reste le meilleur choix",
  "app.recommend.returnedCopy": "{symbol} {suit} donne {returned} %, aucune autre couleur ne fait mieux dans la simulation.",
  "app.recommend.risky": " Même le meilleur choix reste statistiquement risqué.",
  "app.recommend.personal": " Le verdict intègre votre profil appris ; les cartes affichent aussi la probabilité statistique brute.",
  "app.confidence": "Estimation conditionnée par la position et les passes précédentes. Échantillon effectif minimal : {count} donnes pondérées. La marge affichée est un intervalle statistique indicatif à 95 % ; elle ne mesure pas l’écart entre le moteur et le jeu réel des joueurs.",
  "app.error.hand-size": "La main doit contenir exactement cinq cartes.",
  "app.error.center-required": "La carte retournée est obligatoire.",
  "app.error.duplicate-card": "Une carte ne peut pas être sélectionnée deux fois.",
  "app.error.unknown-position": "La position est inconnue.",
  "practice.kicker": "Table d’entraînement locale",
  "practice.title": "Vous contre le moteur",
  "practice.us": "Vous + partenaire",
  "practice.them": "Le moteur",
  "practice.new": "Nouvelle donne",
  "practice.table": "Table de belote",
  "practice.partner": "Votre partenaire",
  "practice.west": "Adversaire Ouest",
  "practice.east": "Adversaire Est",
  "practice.you": "Vous",
  "practice.status.initial": "Lancez une donne.",
  "practice.seat.south": "Vous",
  "practice.seat.west": "Adversaire Ouest",
  "practice.seat.north": "Votre partenaire",
  "practice.seat.east": "Adversaire Est",
  "practice.waiting": "En attente",
  "practice.toPlay": "À jouer",
  "practice.dealerAria": "Donneur",
  "practice.trumpAria": "Atout {suit}",
  "practice.objective": "Objectif {target}",
  "practice.bidding": "Objectif {target} · enchères tour {round}",
  "practice.contract": "Objectif {target} · {symbol} {player} prend",
  "practice.upcard": "Retourne",
  "practice.won": "Gagné",
  "practice.lost": "Perdu",
  "practice.dealPoints": "Points de la donne",
  "practice.teamUs": "Nous {points}",
  "practice.teamThem": "Adversaires {points}",
  "practice.previous": "Pli précédent",
  "practice.currentTrick": "Pli en cours",
  "practice.handAria": "Votre main",
  "practice.previousResult": "{player} · {points} pts",
  "practice.take": "Prendre {symbol}",
  "practice.pass": "Passer",
  "practice.yourLead": "À vous d’entamer · pli {trick}/8.",
  "practice.yourPlay": "À vous · vous jouez {position}e · entame {leader}.",
  "practice.aiLead": "{player} entame…",
  "practice.aiPlay": "{player} joue {position}e · entame {leader}.",
  "practice.trickWinner": "{player} remporte le pli.",
  "practice.passedOut": "Tout le monde a passé. Redistribution…",
  "practice.matchWinner": "{winner} gagne la partie en {target} points · {us} à {them}.",
  "practice.dealWinner": "{winner} gagne la donne · prochaine donne automatique.",
  "practice.winnerYou": "Vous",
  "practice.winnerThem": "Les adversaires",
  "practice.noAction": "Aucune action.",
  "practice.history.deal": "Donne {deal} · {dealer} donne",
  "practice.history.pass": "{player} passe",
  "practice.history.take": "{player} prend {suit}",
  "practice.history.play": "{player} joue {card}",
  "practice.history.trick": "{player} gagne le pli ({points})",
  "practice.history.win": "Votre équipe gagne la donne",
  "practice.history.loss": "Les adversaires gagnent la donne",
  "practice.profile.summary": "{decisions} décisions · {deals} donnes",
  "practice.profile.direct": "Prise directe",
  "practice.profile.trumpLead": "Entame atout",
  "practice.profile.aceDuck": "Passe de l’As",
  "practice.profile.tenHold": "10 conservé",
  "practice.profile.trumpHold": "Atout conservé",
  "practice.newMatch": "Nouvelle partie",
  "practice.nextDeal": "Donne suivante",
  "observer.kicker": "Assistant local",
  "observer.title": "Journal exhaustif & apprentissage",
  "observer.capture": "Capture en direct",
  "observer.ready": "Prêt · traitement local",
  "observer.waiting": "En attente",
  "observer.noSource": "Aucune source observée",
  "observer.noSourceDetail": "Partagez l’onglet de jeu ou utilisez la caméra.",
  "observer.share": "Partager l’onglet de jeu",
  "observer.camera": "Caméra",
  "observer.stop": "Arrêter",
  "observer.captureAction": "Capturer",
  "observer.auto": "Journal exhaustif : analyser jusqu’à 12 états par seconde",
  "observer.privacy": "Les captures sont temporaires et supprimées automatiquement en fin de partie. La chronologie structurée conserve les données reconnues et leur confiance. Aucun serveur ni cloud.",
  "observer.journalKicker": "Journal couplé aux probabilités",
  "observer.journalTitle": "Parties enregistrées",
  "observer.emptyStats": "0 situation",
  "observer.export": "Exporter JSON",
  "observer.clear": "Effacer le journal",
  "observer.empty": "Aucune situation enregistrée pour le moment."
  ,"observer.phase.session-start": "Session démarrée"
  ,"observer.phase.session-end": "Session terminée"
  ,"observer.phase.game-start": "Partie détectée"
  ,"observer.phase.game-end": "Partie terminée"
  ,"observer.phase.deal-start": "Donne détectée"
  ,"observer.phase.deal-end": "Donne terminée"
  ,"observer.phase.visible-state": "État de jeu reconnu"
  ,"observer.phase.practice-result": "Donne d’entraînement"
  ,"observer.phase.lobby": "Salon détecté"
  ,"observer.phase.analysis": "Analyse de prise"
  ,"observer.phase.bidding": "Enchère détectée"
  ,"observer.phase.play": "Pli détecté"
  ,"observer.phase.result": "Résultat détecté"
  ,"observer.phase.table": "Table détectée"
  ,"observer.phase.unknown": "Capture"
  ,"observer.decision.placeholder": "Décision à annoter"
  ,"observer.decision.pass": "Passer"
  ,"observer.decision.take": "Prendre {symbol}"
  ,"observer.decision.play": "Jouer {card}"
  ,"observer.outcome.placeholder": "Résultat"
  ,"observer.outcome.win": "Gagnée"
  ,"observer.outcome.loss": "Perdue"
  ,"observer.profile.heading": "Profil personnel"
  ,"observer.profile.results": "{count} / 20 résultats"
  ,"observer.profile.pending": "Le moteur statistique reste seul actif. Cinq résultats minimum sont nécessaires."
  ,"observer.profile.active": "Calibration personnelle active · {count} décisions apprises{favorite}."
  ,"observer.profile.favorite": " · préférence {symbol}"
  ,"observer.profile.conventions": "Conventions : appel direct · passe de l’As au 1er tour avec exactement 2 cartes, jamais si risque de capot · coup forcé {seconds} s."
  ,"observer.summary.noHand": "Main non annotée"
  ,"observer.summary.upcard": "· retourne {card}"
  ,"observer.summary.position": "· pos. {position}"
  ,"observer.stats": "{sessions} sessions · {games} parties · {deals} donnes · {events} événements · {annotated} annotés · {images} captures temporaires · {megabytes} Mo"
  ,"observer.captureAlt": "Capture de la situation"
  ,"observer.decisionAria": "Décision observée"
  ,"observer.outcomeAria": "Résultat de la donne"
  ,"observer.link": "Lier"
  ,"observer.linkTitle": "Lier les cartes actuellement saisies"
  ,"observer.deleteTitle": "Supprimer cette situation"
  ,"observer.status.gameSaved": "Partie {game} enregistrée · {count} captures supprimées"
  ,"observer.status.gameProgress": "Partie {game} · {count} états enregistrés"
  ,"observer.status.stopped": "Capture arrêtée · données conservées localement"
  ,"observer.status.permission": "Autorisation de capture en attente…"
  ,"observer.status.screen": "Journal exhaustif actif · écran local"
  ,"observer.status.camera": "Journal exhaustif actif · caméra locale"
  ,"observer.status.denied": "Autorisation refusée"
  ,"observer.status.unavailable": "Capture indisponible"
  ,"observer.status.exporting": "Préparation de l’export…"
  ,"observer.status.exported": "Export JSON créé · aucune capture incluse"
  ,"observer.status.clearConfirm": "Supprimer tout le journal local d’apprentissage ?"
  ,"observer.status.cleared": "Journal local effacé"
  ,"observer.status.storage": "Stockage local indisponible"
  ,"observer.status.invalidState": "État visible non exploitable"
});

export const en = Object.freeze({
  "meta.title": "Worth taking? — Belote calculator",
  "meta.description": "Estimate a classic belote bid and practise with the same engine, with no account.",
  "nav.home": "Worth taking? home",
  "nav.practice": "PLAY A DEAL",
  "nav.observer": "OBSERVE & LEARN",
  "nav.install": "INSTALL THE APP",
  "nav.demo": "TRY THE APP",
  "nav.rule": "Classic belote · no declarations",
  "nav.language": "Language",
  "demo.banner": "Demo — fictional data",
  "demo.detail": "Same engine, rules and build. This session’s data is isolated on this device.",
  "demo.reset": "RESET THE DEMO",
  "demo.exit": "EXIT THE DEMO",
  "hero.eyebrow": "Bid assistant",
  "hero.title": "Choose the right trump,<br><em>before you say yes.</em>",
  "hero.copy": "Enter your five cards, the upcard and your seat. The simulator plays thousands of possible deals to estimate your chance of making the contract.",
  "calculator.kicker": "Your deal",
  "calculator.title": "Build your hand",
  "calculator.reset": "Clear all",
  "calculator.hand": "My hand",
  "calculator.center": "The upcard",
  "calculator.fillZone": "Area to fill",
  "calculator.cardPicker": "Card selection",
  "calculator.positionAria": "Position at the table",
  "calculator.placeKicker": "Your seat",
  "calculator.placeTitle": "Where are you sitting?",
  "calculator.dealer": "Dealer",
  "calculator.first": "First",
  "calculator.partner": "Dealer’s partner",
  "calculator.last": "Last",
  "calculator.precision": "Precision",
  "calculator.precision.fast": "Fast · 2,000 deals",
  "calculator.precision.balanced": "Balanced · 5,000 deals",
  "calculator.precision.fine": "Fine · 10,000 deals",
  "calculator.auto": "Automatic calculation",
  "calculator.auto.initial": "Select your cards and seat",
  "calculator.progress": "Simulation running…",
  "results.kicker": "Verdict",
  "results.title": "What the deal says",
  "method.summary": "How is the probability calculated?",
  "method.first": "Each simulation completes the four hands at random, then plays all eight tricks while respecting following suit, trumping, overtrumping, card order and the last-trick bonus. Your position and prior passes weight the plausible deals.",
  "method.second": "The contract succeeds when the taking team beats the defence. Belote-rebelote counts; sequences and four-of-a-kind do not. This remains decision support: real players’ styles can change the outcome.",
  "footer.product": "Bid calculator · 4-player belote",
  "footer.rules": "32 cards · 2 teams · 8 tricks",
  "footer.version": "Version {version} · build {buildId}",
  "install.title": "Install Worth taking?",
  "install.ready": "Add the app to your home screen for a full-screen, offline-ready experience.",
  "install.ios": "In Safari, tap Share, then “Add to Home Screen”.",
  "install.desktop": "Open your browser menu and choose “Install app”.",
  "install.confirm": "INSTALL NOW",
  "install.close": "Later",
  "install.done": "The app is already installed.",
  "common.close": "Close",
  "common.none": "—",
  "common.error": "Something went wrong.",
  "common.points": "{value} average points",
  "common.tricks": "{value} / 8 average tricks",
  "common.margin": "± {value} estimated margin",
  "common.percent": "{value} percent",
  "suit.clubs": "Clubs",
  "suit.diamonds": "Diamonds",
  "suit.hearts": "Hearts",
  "suit.spades": "Spades",
  "app.running": "Simulation running…",
  "app.missing.one": "{count} more card",
  "app.missing.other": "{count} more cards",
  "app.choosePlace": "Choose your seat",
  "app.ready": "Ready to recalculate after every change",
  "app.cardLabel": "{rank} of {suit}",
  "app.personalProfile": "Personal profile {value}%",
  "app.result.returned": "Taking the upcard suit",
  "app.result.alternative": "Best other suit",
  "app.comparison": "All four trump suits",
  "app.returnedSuffix": " · upcard",
  "app.personalSuffix": "{value}% personal",
  "app.simulations": "{count} deals per suit",
  "app.verdict": "Your bid from position {position}",
  "app.recommend.alternativeTitle": "{symbol} {suit} is the best option",
  "app.recommend.alternativeCopy": "{alternative}% versus {returned}% in the upcard suit. You would need to pass round one to choose it.",
  "app.recommend.returnedTitle": "The upcard suit remains the best choice",
  "app.recommend.returnedCopy": "{symbol} {suit} reaches {returned}%; no other suit performs better in the simulation.",
  "app.recommend.risky": " Even the best option remains statistically risky.",
  "app.recommend.personal": " The verdict includes your learned profile; cards still show the raw engine probability.",
  "app.confidence": "Estimate conditioned on position and prior passes. Minimum effective sample: {count} weighted deals. The margin is an indicative 95% statistical interval; it does not measure the gap between the engine and real players.",
  "app.error.hand-size": "The hand must contain exactly five cards.",
  "app.error.center-required": "The upcard is required.",
  "app.error.duplicate-card": "The same card cannot be selected twice.",
  "app.error.unknown-position": "The seat is unknown.",
  "practice.kicker": "Local practice table",
  "practice.title": "You versus the engine",
  "practice.us": "You + partner",
  "practice.them": "The engine",
  "practice.new": "New deal",
  "practice.table": "Belote table",
  "practice.partner": "Your partner",
  "practice.west": "West opponent",
  "practice.east": "East opponent",
  "practice.you": "You",
  "practice.status.initial": "Start a deal.",
  "practice.seat.south": "You",
  "practice.seat.west": "West opponent",
  "practice.seat.north": "Your partner",
  "practice.seat.east": "East opponent",
  "practice.waiting": "Waiting",
  "practice.toPlay": "To play",
  "practice.dealerAria": "Dealer",
  "practice.trumpAria": "Trump {suit}",
  "practice.objective": "Target {target}",
  "practice.bidding": "Target {target} · bidding round {round}",
  "practice.contract": "Target {target} · {symbol} {player} takes",
  "practice.upcard": "Upcard",
  "practice.won": "Won",
  "practice.lost": "Lost",
  "practice.dealPoints": "Deal points",
  "practice.teamUs": "Us {points}",
  "practice.teamThem": "Opponents {points}",
  "practice.previous": "Previous trick",
  "practice.currentTrick": "Current trick",
  "practice.handAria": "Your hand",
  "practice.previousResult": "{player} · {points} pts",
  "practice.take": "Take {symbol}",
  "practice.pass": "Pass",
  "practice.yourLead": "Your lead · trick {trick}/8.",
  "practice.yourPlay": "Your turn · playing {position} · led by {leader}.",
  "practice.aiLead": "{player} leads…",
  "practice.aiPlay": "{player} plays {position} · led by {leader}.",
  "practice.trickWinner": "{player} wins the trick.",
  "practice.passedOut": "Everyone passed. Redealing…",
  "practice.matchWinner": "{winner} win the {target}-point match · {us} to {them}.",
  "practice.dealWinner": "{winner} win the deal · next deal starts automatically.",
  "practice.winnerYou": "You",
  "practice.winnerThem": "The opponents",
  "practice.noAction": "No action yet.",
  "practice.history.deal": "Deal {deal} · {dealer} deals",
  "practice.history.pass": "{player} passes",
  "practice.history.take": "{player} takes {suit}",
  "practice.history.play": "{player} plays {card}",
  "practice.history.trick": "{player} wins the trick ({points})",
  "practice.history.win": "Your team wins the deal",
  "practice.history.loss": "The opponents win the deal",
  "practice.profile.summary": "{decisions} decisions · {deals} deals",
  "practice.profile.direct": "First-round takes",
  "practice.profile.trumpLead": "Trump leads",
  "practice.profile.aceDuck": "Ace ducked",
  "practice.profile.tenHold": "Ten held",
  "practice.profile.trumpHold": "Trump held",
  "practice.newMatch": "New match",
  "practice.nextDeal": "Next deal",
  "observer.kicker": "Local assistant",
  "observer.title": "Detailed journal & learning",
  "observer.capture": "Live capture",
  "observer.ready": "Ready · local processing",
  "observer.waiting": "Waiting",
  "observer.noSource": "No source observed",
  "observer.noSourceDetail": "Share the game tab or use your camera.",
  "observer.share": "Share game tab",
  "observer.camera": "Camera",
  "observer.stop": "Stop",
  "observer.captureAction": "Capture",
  "observer.auto": "Detailed journal: analyse up to 12 states per second",
  "observer.privacy": "Captures are temporary and automatically deleted at the end of a game. The structured timeline retains recognised data and confidence. No server or cloud.",
  "observer.journalKicker": "Probability-linked journal",
  "observer.journalTitle": "Recorded games",
  "observer.emptyStats": "0 situations",
  "observer.export": "Export JSON",
  "observer.clear": "Clear journal",
  "observer.empty": "No situation recorded yet."
  ,"observer.phase.session-start": "Session started"
  ,"observer.phase.session-end": "Session ended"
  ,"observer.phase.game-start": "Game detected"
  ,"observer.phase.game-end": "Game ended"
  ,"observer.phase.deal-start": "Deal detected"
  ,"observer.phase.deal-end": "Deal ended"
  ,"observer.phase.visible-state": "Game state recognised"
  ,"observer.phase.practice-result": "Practice deal"
  ,"observer.phase.lobby": "Lobby detected"
  ,"observer.phase.analysis": "Bid analysis"
  ,"observer.phase.bidding": "Bidding detected"
  ,"observer.phase.play": "Trick detected"
  ,"observer.phase.result": "Result detected"
  ,"observer.phase.table": "Table detected"
  ,"observer.phase.unknown": "Capture"
  ,"observer.decision.placeholder": "Decision to annotate"
  ,"observer.decision.pass": "Pass"
  ,"observer.decision.take": "Take {symbol}"
  ,"observer.decision.play": "Play {card}"
  ,"observer.outcome.placeholder": "Result"
  ,"observer.outcome.win": "Won"
  ,"observer.outcome.loss": "Lost"
  ,"observer.profile.heading": "Personal profile"
  ,"observer.profile.results": "{count} / 20 results"
  ,"observer.profile.pending": "Only the statistical engine is active. At least five results are required."
  ,"observer.profile.active": "Personal calibration active · {count} decisions learned{favorite}."
  ,"observer.profile.favorite": " · preference {symbol}"
  ,"observer.profile.conventions": "Conventions: direct call · duck the Ace on the first round with exactly 2 cards, never with capot risk · forced move {seconds}s."
  ,"observer.summary.noHand": "Hand not annotated"
  ,"observer.summary.upcard": "· upcard {card}"
  ,"observer.summary.position": "· pos. {position}"
  ,"observer.stats": "{sessions} sessions · {games} games · {deals} deals · {events} events · {annotated} annotated · {images} temporary captures · {megabytes} MB"
  ,"observer.captureAlt": "Situation capture"
  ,"observer.decisionAria": "Observed decision"
  ,"observer.outcomeAria": "Deal result"
  ,"observer.link": "Link"
  ,"observer.linkTitle": "Link the currently selected cards"
  ,"observer.deleteTitle": "Delete this situation"
  ,"observer.status.gameSaved": "Game {game} saved · {count} captures deleted"
  ,"observer.status.gameProgress": "Game {game} · {count} states saved"
  ,"observer.status.stopped": "Capture stopped · data kept locally"
  ,"observer.status.permission": "Waiting for capture permission…"
  ,"observer.status.screen": "Detailed journal active · local screen"
  ,"observer.status.camera": "Detailed journal active · local camera"
  ,"observer.status.denied": "Permission denied"
  ,"observer.status.unavailable": "Capture unavailable"
  ,"observer.status.exporting": "Preparing export…"
  ,"observer.status.exported": "JSON export created · no capture included"
  ,"observer.status.clearConfirm": "Delete the entire local learning journal?"
  ,"observer.status.cleared": "Local journal cleared"
  ,"observer.status.storage": "Local storage unavailable"
  ,"observer.status.invalidState": "Visible state could not be used"
});

export const catalogs = Object.freeze({ fr, en });
